/**
 * Host-aware runtime URL resolution for the myNibble portal.
 *
 * myNibble serves more than one host from the same JS bundle: today
 * `my.nibble.nautilos.app`, tomorrow `my.nibble.it` (cutover). Bake-time
 * `VUE_APP_*` values break this by hardcoding a single host per build.
 *
 * These helpers derive API URL, OIDC callback URL and base domain from
 * `window.location` at runtime. `VUE_APP_*` env vars are kept as
 * **server-side fallback only** (SSR/test contexts without `window`).
 *
 * Priority: runtime `window.location` > `VUE_APP_*` env > localhost.
 *
 * Why runtime wins over env: vue-cli build args may inject `VUE_APP_*` as
 * empty string (`""`) when not provided. With env-priority, webpack
 * DefinePlugin + minifier collapses the utility to a literal `""` at call
 * sites — breaking the very thing this module is designed to fix. Runtime
 * always wins in browser context.
 *
 * Convention: deployment host pattern is `my.{tenant}.{tld}` — the first
 * segment (`my`) is swapped to `api` for the API URL and stripped for the
 * base domain. Mirrors `console/src/plugins/hostAwareConfig.js`.
 *
 * Ref: spec `console-host-aware-callback` v2 (Console), portato in myNibble.
 */

/**
 * Resolve the API URL host-aware.
 *
 * Priority: runtime derivation from `window.location` > `VUE_APP_API_URL`
 * (server-side fallback only) > localhost.
 *
 * Examples (runtime derivation):
 *   - `my.nibble.nautilos.app`   → `https://api.nibble.nautilos.app`
 *   - `my.nibble.it`             → `https://api.nibble.it`
 *
 * @returns {string} Absolute API URL, no trailing slash.
 */
export function getRuntimeApiUrl() {
  if (typeof window !== 'undefined' && window.location && window.location.host) {
    const { protocol, host } = window.location;
    const hostname = host.split(':')[0];
    if (hostname.includes('.') && hostname !== 'localhost') {
      const apiHost = host.replace(/^[^.]+/, 'api');
      return `${protocol}//${apiHost}`;
    }
  }
  return process.env.VUE_APP_API_URL || 'http://localhost:8080';
}

/**
 * Resolve the tenant base domain host-aware.
 *
 * Priority: runtime `window.location.host` with first segment stripped >
 * `VUE_APP_BASE_DOMAIN` (server-side fallback only) > localhost.
 *
 * Examples:
 *   - `my.nibble.nautilos.app`   → `nibble.nautilos.app`
 *   - `my.nibble.it`             → `nibble.it`
 *
 * @returns {string} Base domain (no scheme, no leading dot).
 */
export function getRuntimeBaseDomain() {
  if (typeof window !== 'undefined' && window.location && window.location.host) {
    const { host } = window.location;
    const hostname = host.split(':')[0];
    if (hostname.includes('.') && hostname !== 'localhost') {
      return host.split('.').slice(1).join('.');
    }
  }
  return process.env.VUE_APP_BASE_DOMAIN || 'nibble.it';
}
